import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrencySelect } from '@/components/ui/currency-select';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useExpenseCategories } from '@/hooks/finance/useExpenseCategories';
import { useCreateExpense } from '@/hooks/finance/useExpenses';
import { useCategoryHierarchy } from '@/hooks/finance/useCategoryHierarchy';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { toast } from 'sonner';
import { BusinessUnitSelect } from '@/components/finance/BusinessUnitSelect';
import { BUAllocationEditor, AllocationValue, allocationsAreValid } from '@/components/finance/BUAllocationEditor';
import { DEFAULT_BUSINESS_UNIT, BusinessUnit } from '@/constants/businessUnits';
import { supabase } from '@/integrations/supabase/client';

type VatMode = 'none' | 'add' | 'deduct';

const NewExpensePage = () => {
  const navigate = useNavigate();
  const { data: departments } = useDepartments();
  const { data: categories } = useExpenseCategories();
  const { groupedCategories } = useCategoryHierarchy(categories);
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const { data: defaultVatRate = 15 } = useVatRate();
  const { data: withholdingRates = [] } = useActiveWithholdingRates();
  const createExpense = useCreateExpense();

  const [formData, setFormData] = useState({
    department_id: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_date: new Date().toISOString().split('T')[0],
    description: '',
    vendor_name: '',
    reference_number: '',
    payment_reference: '',
    amount: 0,
    payment_method: '',
    currency: DEFAULT_CURRENCY,
    bank_account_id: '',
    business_unit: DEFAULT_BUSINESS_UNIT as BusinessUnit,
    coverage_months: 1,
  });

  const [vatMode, setVatMode] = useState<VatMode>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buAllocations, setBuAllocations] = useState<AllocationValue[]>([]);
  const [splitMode, setSplitMode] = useState<'single' | 'split'>('single');
  const selectedCategoryPath = Array.from(groupedCategories.values())
    .flat()
    .find((cat) => cat.id === formData.category_id)?.hierarchyPath;

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate all totals based on VAT mode and withholding
  const paymentTotals = useMemo(() => {
    const enteredAmount = Number(formData.amount) || 0;
    const vatRateDecimal = defaultVatRate / 100;

    let baseAmount: number;
    let vatAmount: number;
    let subtotal: number;

    switch (vatMode) {
      case 'add':
        baseAmount = enteredAmount;
        vatAmount = baseAmount * vatRateDecimal;
        subtotal = baseAmount + vatAmount;
        break;
      case 'deduct':
        baseAmount = enteredAmount / (1 + vatRateDecimal);
        vatAmount = enteredAmount - baseAmount;
        subtotal = enteredAmount;
        break;
      default:
        baseAmount = enteredAmount;
        vatAmount = 0;
        subtotal = enteredAmount;
    }

    const selectedRate = withholdingApplicable && selectedWithholdingRateId
      ? withholdingRates.find(r => r.id === selectedWithholdingRateId)
      : null;
    const withholdingRate = selectedRate?.rate || 0;
    const withholdingAmount = baseAmount * (withholdingRate / 100);
    const netPayment = subtotal - withholdingAmount;

    return {
      baseAmount,
      vatRate: vatMode !== 'none' ? defaultVatRate : 0,
      vatAmount,
      subtotal,
      withholdingRate,
      withholdingAmount,
      netPayment,
    };
  }, [formData.amount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  const handleSubmit = async () => {
    if (!formData.description || !formData.expense_date || formData.amount <= 0 || !formData.payment_reference.trim()) {
      toast.error('Please fill in required fields (including Payment Reference)');
      return;
    }
    if (splitMode === 'split' && !allocationsAreValid(buAllocations)) {
      toast.error('Business unit allocations must sum to 100%');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await createExpense.mutateAsync({
        ...formData,
        department_id: formData.department_id || undefined,
        category_id: formData.category_id || undefined,
        bank_account_id: formData.bank_account_id || undefined,
        amount: paymentTotals.baseAmount,
        vat_rate: paymentTotals.vatRate,
        vat_amount: paymentTotals.vatAmount,
        total_amount: paymentTotals.subtotal,
        withholding_rate: paymentTotals.withholdingRate,
        withholding_amount: paymentTotals.withholdingAmount,
        net_payment: paymentTotals.netPayment,
        coverage_months: Math.max(1, Number(formData.coverage_months) || 1),
      });
      if (splitMode === 'split' && buAllocations.length > 0 && (created as any)?.id) {
        const { error: allocErr } = await (supabase as any).rpc('set_expense_bu_allocations', {
          p_expense_id: (created as any).id,
          p_allocations: buAllocations,
        });
        if (allocErr) toast.error(`Saved expense, but allocations failed: ${allocErr.message}`);
      }
      navigate('/finance/accounting/expenses');
    } catch (error) {
      console.error('Failed to create expense:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Record Expense</h1>
            <p className="text-muted-foreground">Add a new expense record</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Expense Details</CardTitle>
            <CardDescription>Enter the expense information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="expense_date">Expense Date *</Label>
                <Input
                  id="expense_date"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => handleInputChange('expense_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_date">Payment Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => handleInputChange('payment_date', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coverage_months">Coverage Months</Label>
              <Input
                id="coverage_months"
                type="number"
                min={1}
                step={1}
                value={formData.coverage_months}
                onChange={(e) => handleInputChange('coverage_months', Number(e.target.value) || 1)}
              />
              <p className="text-xs text-muted-foreground">
                Spread this expense straight-line over N months in the P&amp;L (e.g. 3 for quarterly rent). Default 1.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(v) => {
                    const cat = categories?.find((c) => c.id === v);
                    const defaultBU = (cat as any)?.default_business_unit as BusinessUnit | null | undefined;
                    setFormData((prev) => ({
                      ...prev,
                      category_id: v,
                      business_unit: defaultBU || prev.business_unit,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(groupedCategories.entries()).map(([groupName, cats]) => (
                      <SelectGroup key={groupName}>
                        <SelectLabel className="font-semibold">{groupName}</SelectLabel>
                        {cats.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.hierarchyPath}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Business Unit Assignment *</Label>
                <RadioGroup
                  value={splitMode}
                  onValueChange={(v) => setSplitMode(v as 'single' | 'split')}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="single" id="bu-single" />
                    <Label htmlFor="bu-single" className="font-normal cursor-pointer text-sm">Single BU</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="split" id="bu-split" />
                    <Label htmlFor="bu-split" className="font-normal cursor-pointer text-sm">Split across BUs</Label>
                  </div>
                </RadioGroup>
                {splitMode === 'single' ? (
                  <BusinessUnitSelect
                    value={formData.business_unit}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, business_unit: v }))}
                  />
                ) : (
                  <div className="rounded-md border p-3">
                    <BUAllocationEditor value={buAllocations} onChange={setBuAllocations} compact />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select
                  value={formData.department_id}
                  onValueChange={(v) => handleInputChange('department_id', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments?.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="What was this expense for?"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vendor_name">Vendor / Payee</Label>
                <Input
                  id="vendor_name"
                  placeholder="Who was paid"
                  value={formData.vendor_name}
                  onChange={(e) => handleInputChange('vendor_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference_number">Reference / Invoice #</Label>
                <Input
                  id="reference_number"
                  placeholder="Invoice or receipt number"
                  value={formData.reference_number}
                  onChange={(e) => handleInputChange('reference_number', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_reference">Payment Reference *</Label>
                <Input
                  id="payment_reference"
                  placeholder="Bank transaction ref / check number"
                  value={formData.payment_reference}
                  onChange={(e) => handleInputChange('payment_reference', e.target.value)}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    value={formData.amount || ''}
                    onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <CurrencySelect
                    value={formData.currency}
                    onValueChange={(v) => handleInputChange('currency', v)}
                  />
                </div>
              </div>

              {/* VAT Mode Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">VAT Handling</Label>
                <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="vat-none" />
                    <Label htmlFor="vat-none" className="font-normal cursor-pointer">No VAT</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="add" id="vat-add" />
                    <Label htmlFor="vat-add" className="font-normal cursor-pointer">
                      Add VAT ({defaultVatRate}%) – amount is base
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="deduct" id="vat-deduct" />
                    <Label htmlFor="vat-deduct" className="font-normal cursor-pointer">
                      Deduct VAT ({defaultVatRate}%) – amount includes VAT
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Withholding Tax */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={withholdingApplicable}
                    onCheckedChange={setWithholdingApplicable}
                  />
                  <Label>Apply Withholding Tax</Label>
                </div>
                {withholdingApplicable && (
                  <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select withholding rate" />
                    </SelectTrigger>
                    <SelectContent>
                      {withholdingRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <Separator />

              {/* Payment Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Amount</span>
                  <span>{formatCurrency(paymentTotals.baseAmount, formData.currency)}</span>
                </div>
                {vatMode !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">VAT ({defaultVatRate}%)</span>
                    <span>+ {formatCurrency(paymentTotals.vatAmount, formData.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-1 border-t">
                  <span>Total Expense</span>
                  <span>{formatCurrency(paymentTotals.subtotal, formData.currency)}</span>
                </div>
                {withholdingApplicable && selectedWithholdingRateId && (
                  <>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Withholding ({paymentTotals.withholdingRate}%)</span>
                      <span>- {formatCurrency(paymentTotals.withholdingAmount, formData.currency)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base pt-1 border-t">
                      <span>Net Payment</span>
                      <span>{formatCurrency(paymentTotals.netPayment, formData.currency)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(v) => handleInputChange('payment_method', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="How was it paid?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="petty_cash">Petty Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_account">Paid From Account</Label>
              <Select
                value={formData.bank_account_id}
                onValueChange={(v) => handleInputChange('bank_account_id', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={bankAccountsLoading ? "Loading..." : "Select bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            Record Expense
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default NewExpensePage;
